
import React, { useState } from 'react';
import { ProjectStatus, ViewState, Project, CarouselItem } from '../types';
import ProjectCarousel from './ProjectCarousel';

interface ProjectSectionProps {
  limit?: number;
  setView: (view: ViewState) => void;
  projects: Project[];
  carouselItems?: CarouselItem[];
}

const ProjectSection: React.FC<ProjectSectionProps> = ({ limit, setView, projects, carouselItems = [] }) => {
  const [filter, setFilter] = useState<'all' | ProjectStatus>('all');
  
  const displayedProjects = limit 
    ? projects.slice(0, limit) 
    : filter === 'all' 
      ? projects 
      : projects.filter(p => p.status === filter);

  return (
    <section className="py-20 px-4 max-w-7xl mx-auto transition-colors">
      {!limit && carouselItems.length > 0 && (
        <ProjectCarousel items={carouselItems} />
      )}

      <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
        <div className="max-w-2xl">
            <h2 className="text-blue-800 dark:text-blue-400 font-black text-[9px] uppercase tracking-[0.4em] mb-2">
              Our Portfolio
            </h2>
            <h3 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white uppercase tracking-tighter leading-none mb-4">
              Infrastructure <span className="text-blue-600">Excellence</span>
            </h3>
        </div>
        
        {!limit && (
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg transition-colors">
            {(['all', ProjectStatus.COMPLETED, ProjectStatus.ONGOING] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition ${
                  filter === f 
                  ? 'bg-white dark:bg-gray-700 text-blue-800 dark:text-blue-300 shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {displayedProjects.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 transition-colors">
          <p className="text-gray-500 dark:text-gray-400 font-medium">No projects to display at this time.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          {displayedProjects.map(project => (
            <div key={project.id} className="group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-xl dark:hover:shadow-blue-900/10 transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={project.imageUrl} 
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  onError={(e) => { (e.target as HTMLImageElement).src = 'https://picsum.photos/id/1070/800/600'; }}
                />
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg ${
                  project.status === ProjectStatus.COMPLETED ? 'bg-green-500 text-white' : 'bg-blue-600 dark:bg-blue-500 text-white'
                }`}>
                  {project.status}
                </div>
              </div>
              
              <div className="p-8">
                <div className="text-blue-600 dark:text-blue-400 text-sm font-bold mb-1 transition-colors">{project.location}</div>
                <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 transition-colors">{project.title}</h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6 line-clamp-2 transition-colors">{project.description}</p>
                
                <div className="grid grid-cols-2 gap-4 border-t border-gray-50 dark:border-gray-700 pt-6 transition-colors">
                  {project.stats.map((stat, idx) => (
                    <div key={idx}>
                      <div className="text-xs text-gray-400 dark:text-gray-500 uppercase font-bold">{stat.label}</div>
                      <div className="text-lg font-bold text-blue-900 dark:text-blue-300 transition-colors">{stat.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {limit && projects.length > limit && (
        <div className="mt-16 text-center">
          <button 
            onClick={() => setView('projects')}
            className="inline-flex items-center gap-2 font-bold text-blue-800 dark:text-blue-400 hover:gap-4 transition-all"
          >
            Explore Entire Portfolio
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
};

export default ProjectSection;
